import { createBrowserHistory, History } from 'history';
import { configureStore } from '@reduxjs/toolkit';
import { Hub } from '@aws-amplify/core';
import { CognitoConfiguration, getConfiguration } from './GlobalFunctions';
import {
  dataResidencyManager,
  initializeDataResidencyAwareAuth,
} from '@peekapak/data-residency';
import user from './UserManagement';
import game from './GameState';
import entry from './EntryState';
import signup from './SignUpState';
import events, { push, AuthEvent } from './AuthenticationEvents';

const store = configureStore({
  reducer: {
    user,
    game,
    entry,
    signup,
    events,
  },
  // CAUTION: we are explicitly turning off the warnings about putting in
  // non-serizliable objects into the store. This is because we are using
  // the CognitoUserProfileType which is an object with functions that we want
  // access to such as getSignInUserSession.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const myBrowserHistory: History = createBrowserHistory();

// Deprecated: Use getCognitoConfiguration() instead for data residency support
export const cognitoConfiguration = getConfiguration(
  'cognito',
) as CognitoConfiguration;

// Get data residency-aware Cognito configuration
export async function getCognitoConfiguration(): Promise<CognitoConfiguration> {
  type RawCognitoConfiguration = {
    userPool?: string;
    userPoolId?: string;
    identityPool: string;
    clientId: string;
    region: string;
    hostedUICognitoDomain?: string;
    hostedUISignInCallbackPath?: string;
    hostedUISignOutCallbackPath?: string;
  };

  const dataResidency = dataResidencyManager.getCurrentDataResidency();
  const configModule =
    await dataResidencyManager.getCognitoConfiguration(dataResidency);
  const config = (configModule.default ||
    configModule) as RawCognitoConfiguration;

  // Map to expected CognitoConfiguration interface
  return {
    userPool:
      config.userPool ||
      config.userPoolId ||
      getConfiguration('cognito').userPool,
    identityPool: config.identityPool,
    clientId: config.clientId,
    region: config.region,
    hostedUICognitoDomain:
      config.hostedUICognitoDomain ||
      getConfiguration('cognito').hostedUICognitoDomain,
    hostedUISignInCallbackPath:
      config.hostedUISignInCallbackPath ||
      getConfiguration('cognito').hostedUISignInCallbackPath ||
      '/oauth/login',
    hostedUISignOutCallbackPath:
      config.hostedUISignOutCallbackPath ||
      getConfiguration('cognito').hostedUISignOutCallbackPath ||
      '/',
  };
}

// The Amplify Hub is app-global state, like the store, so the app owns the
// wiring. Registered once at module load - before initializeApplicationState
// runs - so no auth event can fire into a listener that is not yet attached.
Hub.listen('auth', pushEventIntoQueue);

/**
 * Resolve this browser's data residency and configure Amplify for it.
 *
 * Must be AWAITED before the app renders. App.componentDidMount reactivates the
 * session, and if that runs while this is still in flight the two race to
 * configure Amplify for possibly different regions.
 *
 * There is deliberately no legacy fallback here any more. The one that used to
 * live in this file called Amplify.configure with the US-only pool ids from
 * configuration.json and WITHOUT the `cookieStorage` block - so it could not
 * serve an ARE account at all, and it silently moved the Cognito tokens from
 * cookies to localStorage, which is exactly where the peekapak.com -> /play
 * session handoff reads them from. A failure here must surface, not quietly
 * degrade into a session the parent app cannot hand over to.
 */
export async function initializeApplicationState() {
  await initializeDataResidencyAwareAuth();
}

function pushEventIntoQueue({ payload }: { payload: AuthEvent }) {
  console.info(
    `%c OauthLogin pushing event `,
    'background: #222; color: #bada55',
    payload,
  );

  getStore().dispatch(push(payload));
}

export function getStore() {
  return store;
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
